const MIN_TOKENIZE_NAME_LENGTH = 2;
const MIN_NAME_PART_LENGTH = 4;
const SLOT_COUNT = 3;
const DEFAULT_LETTER_TO_SLOT = { A: 1, B: 2, C: 3 };

export const escapeRegExp = (value) => String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const pickSlotCharacterName = (character) => {
  if (!character || typeof character !== 'object') return '';
  const raw = character.name || character.character_name || character.char_name || '';
  return typeof raw === 'string' ? raw.trim() : '';
};

export const rosterHasResolvableNames = (characters) =>
  (Array.isArray(characters) ? characters : []).some((character) => pickSlotCharacterName(character).length > 0);

const namesBySlotFromRoster = (characters) => {
  const namesBySlot = {};
  (Array.isArray(characters) ? characters : []).forEach((character, index) => {
    if (index >= SLOT_COUNT) return;
    const slot = Number(character?.position) || index + 1;
    const name = pickSlotCharacterName(character);
    if (slot >= 1 && slot <= SLOT_COUNT && name) namesBySlot[slot] = name;
  });
  return namesBySlot;
};

export const replaceNameWithTokenInText = (text, name, token) => {
  if (!text || !name || !token) return text;
  const trimmed = String(name).trim();
  if (trimmed.length < MIN_TOKENIZE_NAME_LENGTH) return text;
  const pattern = new RegExp(`(?<![\\p{L}\\p{N}_])${escapeRegExp(trimmed)}(?![\\p{L}\\p{N}_])`, 'giu');
  return String(text).replace(pattern, token);
};

export const normalizeStoredCharTokens = (text, letterToSlot = DEFAULT_LETTER_TO_SLOT) => {
  if (typeof text !== 'string' || text.length === 0) return text || '';
  let out = text;
  const slotForLetter = (letter) => {
    const upper = String(letter || '').toUpperCase();
    const mapped = Number(letterToSlot?.[upper] ?? DEFAULT_LETTER_TO_SLOT[upper] ?? 1);
    return Number.isFinite(mapped) && mapped >= 1 && mapped <= SLOT_COUNT ? mapped : 1;
  };

  for (let n = SLOT_COUNT; n >= 1; n -= 1) {
    out = out.replace(new RegExp(`\\{\\{char${n}\\}\\}`, 'gi'), `{char${n}}`);
  }

  ['C', 'B', 'A'].forEach((letter) => {
    const slot = slotForLetter(letter);
    out = out.replace(new RegExp(`\\{\\{?char${letter}\\}\\}?`, 'gi'), `{char${slot}}`);
  });

  out = out.replace(/\{\{char\}\}(?![A-C0-9])/gi, '{char1}');
  out = out.replace(/\{char\}(?![A-C0-9])/gi, '{char1}');
  return out;
};

export const tokenizeTextWithSlotNames = (text, namesBySlot, letterToSlot = DEFAULT_LETTER_TO_SLOT) => {
  let out = String(text || '');
  const entries = Object.entries(namesBySlot || {})
    .map(([slot, name]) => ({ slot: Number(slot), name: String(name || '').trim() }))
    .filter((entry) => entry.slot >= 1 && entry.slot <= SLOT_COUNT && entry.name.length >= MIN_TOKENIZE_NAME_LENGTH)
    .sort((a, b) => b.name.length - a.name.length);

  for (const { slot, name } of entries) {
    out = replaceNameWithTokenInText(out, name, `{char${slot}}`);
  }
  return normalizeStoredCharTokens(out, letterToSlot);
};

export const expandCharTokensToNames = (text, namesBySlot) => {
  if (typeof text !== 'string' || text.length === 0) return text || '';
  const names = {
    1: String(namesBySlot?.[1] || namesBySlot?.['1'] || '').trim(),
    2: String(namesBySlot?.[2] || namesBySlot?.['2'] || '').trim(),
    3: String(namesBySlot?.[3] || namesBySlot?.['3'] || '').trim()
  };
  if (!names[2]) names[2] = names[1];
  if (!names[3]) names[3] = names[1];

  let out = normalizeStoredCharTokens(text);
  for (let n = SLOT_COUNT; n >= 1; n -= 1) {
    if (!names[n]) continue;
    out = out.replace(new RegExp(`\\{\\{?char${n}\\}\\}?`, 'gi'), names[n]);
  }
  return out;
};

const rewritePayloadStrings = (payload, rewriter) => {
  const characters = Array.isArray(payload?.characters)
    ? payload.characters.map((character) => ({
      ...character,
      goal: rewriter(character?.goal || ''),
      greeting_intents: Array.isArray(character?.greeting_intents)
        ? character.greeting_intents.map((intent) => rewriter(intent || ''))
        : character?.greeting_intents
    }))
    : payload?.characters;

  return {
    ...payload,
    name: rewriter(payload?.name || ''),
    descr: rewriter(payload?.descr || ''),
    banner_prompt: payload?.banner_prompt == null ? payload?.banner_prompt : rewriter(payload.banner_prompt),
    characters
  };
};

export const tokenizeScenarioPayload = (payload, { letterToSlot = DEFAULT_LETTER_TO_SLOT } = {}) => {
  const namesBySlot = namesBySlotFromRoster(payload?.characters);
  return rewritePayloadStrings(payload, (value) => tokenizeTextWithSlotNames(value, namesBySlot, letterToSlot));
};

export const expandScenarioPayload = (payload) => {
  const namesBySlot = namesBySlotFromRoster(payload?.characters);
  return rewritePayloadStrings(payload, (value) => expandCharTokensToNames(value, namesBySlot));
};

export const catalogNamesDiffer = (suggestedName, resolvedName) => {
  const suggested = String(suggestedName || '').trim().toLowerCase();
  const resolved = String(resolvedName || '').trim().toLowerCase();
  return suggested.length > 0 && resolved.length > 0 && suggested !== resolved;
};

const aliasesForSuggestedName = (name) => {
  const trimmed = String(name || '').trim();
  if (trimmed.length < MIN_TOKENIZE_NAME_LENGTH) return [];
  const aliases = [trimmed];
  trimmed.split(/\s+/).forEach((part) => {
    if (part.length >= MIN_NAME_PART_LENGTH && part.toLowerCase() !== trimmed.toLowerCase()) {
      aliases.push(part);
    }
  });
  return aliases;
};

export const replaceFallbackSuggestedNames = (text, slots) => {
  if (typeof text !== 'string' || text.length === 0) return text;
  const claimed = new Set();
  const replacements = [];
  (Array.isArray(slots) ? slots : []).slice(0, SLOT_COUNT).forEach((entry) => {
    const slot = Number(entry?.slot);
    if (slot < 1 || slot > SLOT_COUNT) return;
    const token = `{char${slot}}`;
    aliasesForSuggestedName(entry?.suggestedName).forEach((alias) => {
      const key = alias.toLowerCase();
      if (claimed.has(key)) return;
      claimed.add(key);
      if (!entry?.fallback) return;
      replacements.push({ alias, token });
    });
  });
  replacements.sort((a, b) => b.alias.length - a.alias.length);
  return replacements.reduce(
    (out, { alias, token }) => replaceNameWithTokenInText(out, alias, token),
    text
  );
};

export { MIN_TOKENIZE_NAME_LENGTH, DEFAULT_LETTER_TO_SLOT };
